import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Snackbar,
  Alert,
  AppBar,
  Toolbar,
  IconButton,
  Container,
  Divider,
  Card,
  CardContent,
  CardHeader,
  Tabs,
  Tab,
  ThemeProvider,
  createTheme
} from '@mui/material';
import { Link } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { addBalanceSheetItem, addCashFlowItem } from '../lib/database';

// Create a separate theme for the input page
const inputPageTheme = createTheme({
  palette: {
    primary: {
      main: '#2196f3',
      light: '#64b5f6',
      dark: '#1976d2',
    },
    secondary: {
      main: '#f50057',
      light: '#ff4081',
      dark: '#c51162',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
        },
      },
    },
  },
});

const InputPage = ({ onDataAdded }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState({
    type: 'balanceSheet',
    category: '',
    amount: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    name: '',
    owner: '',
    frequency: 'one-time'
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const balanceSheetCategories = [
    'Assets',
    'Liabilities',
    'Equity'
  ];

  const cashFlowCategories = [
    'Income',
    'Expenses',
    'Investments',
    'Financing'
  ];

  const frequencies = [
    'one-time',
    'daily',
    'weekly',
    'fortnightly',
    'monthly',
    'quarterly',
    'annually'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        amount: parseFloat(formData.amount)
      };

      if (activeTab === 0) {
        await addBalanceSheetItem(data);
      } else {
        await addCashFlowItem(data);
      }

      setSnackbar({
        open: true,
        message: 'Data added successfully!',
        severity: 'success'
      });

      setFormData({
        type: 'balanceSheet',
        category: '',
        amount: '',
        description: '',
        date: new Date().toISOString().split('T')[0],
        name: '',
        owner: '',
        frequency: 'one-time'
      });

      if (onDataAdded) {
        onDataAdded();
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error adding data. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setFormData(prev => ({
      ...prev,
      type: newValue === 0 ? 'balanceSheet' : 'cashFlow',
      category: ''
    }));
  };

  return (
    <ThemeProvider theme={inputPageTheme}>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <AppBar position="static" elevation={0} sx={{ backgroundColor: 'background.paper', borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              component={Link}
              to="/"
              sx={{ mr: 2, color: 'text.primary' }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: 'text.primary', fontWeight: 600 }}>
              Add Financial Details
            </Typography>
          </Toolbar>
        </AppBar>

        <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
          <Card>
            <CardHeader
              title={
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                  <Tab
                    icon={<AccountBalanceIcon />}
                    label="Balance Sheet"
                    iconPosition="start"
                  />
                  <Tab
                    icon={<TrendingUpIcon />}
                    label="Cash Flow"
                    iconPosition="start"
                  />
                </Tabs>
              }
            />
            <CardContent>
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Owner"
                      name="owner"
                      value={formData.owner}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      fullWidth
                      label="Category"
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      required
                    >
                      {(activeTab === 0 ? balanceSheetCategories : cashFlowCategories).map((category) => (
                        <MenuItem key={category} value={category}>
                          {category}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Amount"
                      name="amount"
                      type="number"
                      value={formData.amount}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  {activeTab === 1 && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        select
                        fullWidth
                        label="Frequency"
                        name="frequency"
                        value={formData.frequency}
                        onChange={handleChange}
                        required
                      >
                        {frequencies.map((freq) => (
                          <MenuItem key={freq} value={freq}>
                            {freq.charAt(0).toUpperCase() + freq.slice(1)}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  )}
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Date"
                      name="date"
                      type="date"
                      value={formData.date}
                      onChange={handleChange}
                      required
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Description"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      multiline
                      rows={2}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="large"
                      fullWidth
                      sx={{ mt: 2 }}
                    >
                      Add Entry
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </CardContent>
          </Card>
        </Container>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
};

export default InputPage; 